import React, {useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// WhatsApp click-to-chat link builder.
//
// Produces the two-line block that goes into a post body / auto-reply:
//     <prefix>
//     https://wa.me/<intl digits>?text=<encoded opening message>
// so the lead's WhatsApp opens with the message already typed.
//
// Only shown for accounts WITHOUT a virtual number. On a VN account contact has
// to route through the tracked link (see the pinned block in GroupsWrapper), and
// a second, untracked number in the post both breaks attribution and trips the
// editor's phone guard.
// ---------------------------------------------------------------------------

export interface WaLinkValues {
  phone: string;    // as typed by the user
  message: string;  // raw, unencoded
  prefix: string;
}

// A wa.me link carrying a Hebrew opening message encodes to ~270 characters —
// every Hebrew letter costs 9 — which swamps the post it sits in. Opting into a
// short link parks the number and the message on the server (POST
// /api/wa-short-link) and puts <base>/w/<code> in the post instead, ~35 chars.
// The destination stays editable afterwards, so re-pointing the code fixes every
// post that already carries it.
export interface WaShortLink {
  code: string;
  url: string;
}

export interface WaLinkPreset extends WaLinkValues {
  _id: string;
  label: string;
  shorten?: boolean;
}

interface Props {
  open: boolean;
  // Pre-fills the form. When `editing` is true the primary button replaces an
  // existing link instead of inserting a new one. `code` means the link being
  // edited is already a short one: the form is filled by resolving it, and
  // saving re-points that same code rather than minting a second one.
  initial?: (Partial<WaLinkValues> & {editing?: boolean; code?: string}) | null;
  onClose: () => void;
  onInsert: (block: string, values: WaLinkValues) => void;
}

const DEFAULT_PREFIX = 'לפרטים בוואטסאפ 👈';

// Length of a minted short link, for the "272 תווים ← 34" hint next to the
// switch. A sample rather than the real thing, which doesn't exist until the
// user opts in — the code length is fixed, so the number is exact.
const SHORT_LINK_SAMPLE_LEN = 'https://api.fbzipper.com/w/AbCd234'.length;

// Israeli-first normalisation to international digits, no '+'. Mirrors
// normaliseWaPhone in routes/api.js — a number saved from either side has to
// come out identical, or the same link would look like two different presets.
export const normaliseWaPhone = (raw: string): string => {
  const d = String(raw || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.indexOf('972') === 0) return d;
  if (d.charAt(0) === '0') return '972' + d.slice(1);
  return d;
};

// Back to a local 0-prefixed number, for display. Presets are stored in intl
// form but Israeli users read 052-…, not 97252-….
export const localiseWaPhone = (intl: string): string => {
  const d = String(intl || '').replace(/\D/g, '');
  return d.indexOf('972') === 0 ? '0' + d.slice(3) : d;
};

export const buildWaLink = (phone: string, message: string): string => {
  const p = normaliseWaPhone(phone);
  if (!p) return '';
  const text = String(message || '').trim();
  return `https://wa.me/${p}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
};

// The two-line block as it lands in the post. Takes the link ready-made so the
// same shape is used whether it's a raw wa.me URL or a short one.
export const buildLinkBlock = (link: string, prefixText: string): string => {
  if (!link) return '';
  const prefix = String(prefixText || '').trim();
  return prefix ? `${prefix}\n${link}` : link;
};

export const buildWaBlock = (v: WaLinkValues): string =>
  buildLinkBlock(buildWaLink(v.phone, v.message), v.prefix);

// Every wa.me / api.whatsapp.com link in a piece of text, with the phone and the
// (still-encoded) prefilled message. Used both to offer "edit the link that's
// already here" and to replace it in place.
export interface FoundWaLink {
  match: string;   // the exact substring, so it can be replaced verbatim
  index: number;
  phone: string;   // intl digits ('' for a short link — the code holds it)
  code?: string;   // set when this is one of our /w/<code> short links
  message: string; // decoded, '' when the link carried no ?text=
}

const WA_LINK_RE = /https?:\/\/(?:wa\.me\/|api\.whatsapp\.com\/send\/?\?phone=)(\d{7,15})((?:[?&][^\s]*)?)/gi;

// Our own short links. Host-agnostic across the fbzipper domains on purpose: the
// base is server config (WA_SHORT_BASE) and moves to a shorter domain later, but
// links minted under the old host stay in old posts and must still be editable.
const WA_SHORT_RE = /https?:\/\/[^\s/]*fbzipper[^\s/]*\/w\/([A-Za-z2-9]{6,20})/gi;

const decodeWaText = (query: string): string => {
  const m = /[?&]text=([^&\s]*)/i.exec(query || '');
  if (!m) return '';
  try {
    // '+' is a space in query strings; decodeURIComponent leaves it alone.
    return decodeURIComponent(m[1].replace(/\+/g, ' '));
  } catch (_) {
    return '';
  }
};

export const findWaLinks = (text: string): FoundWaLink[] => {
  const out: FoundWaLink[] = [];
  const s = String(text || '');
  const re = new RegExp(WA_LINK_RE.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    out.push({match: m[0], index: m.index, phone: m[1], message: decodeWaText(m[2])});
  }
  // Short links carry no number in the URL — the builder resolves the code when
  // it opens. Without this the "there's already a link here" chip would go blank
  // the moment a link was shortened, and the user would be left adding a second
  // one next to the first.
  const shortRe = new RegExp(WA_SHORT_RE.source, 'gi');
  while ((m = shortRe.exec(s)) !== null) {
    out.push({match: m[0], index: m.index, phone: '', message: '', code: m[1]});
  }
  return out.sort((a, b) => a.index - b.index);
};

// The line immediately above a link, when it looks like a label the builder
// wrote (short, no URL of its own) — so re-opening the popup on an existing
// link brings back the prefix too instead of silently dropping it.
export const prefixAboveLink = (text: string, linkIndex: number): string => {
  const before = String(text || '').slice(0, linkIndex);
  const line = before.split('\n').pop() || '';
  // The link sits at the start of its own line: take the line above it.
  const candidate = line.trim() ? line : (before.split('\n').slice(-2)[0] || '');
  const c = candidate.trim();
  if (!c || c.length > 80 || /https?:\/\//i.test(c)) return '';
  return c;
};

const WhatsappLinkModal: React.FC<Props> = ({open, initial, onClose, onInsert}) => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
  const [presets, setPresets] = useState<WaLinkPreset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [saving, setSaving] = useState(false);
  // Which preset the form is currently mirroring — save then updates it in place
  // rather than creating a near-duplicate.
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [saveLabel, setSaveLabel] = useState('');
  const [showSave, setShowSave] = useState(false);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  // Short-link state. `short` is what's currently minted (so the preview shows a
  // real, clickable link); `shorten` is the user's choice. Off by default — the
  // raw wa.me link is what this builder has always produced.
  const [shorten, setShorten] = useState(false);
  const [short, setShort] = useState<WaShortLink | null>(null);
  const [shortening, setShortening] = useState(false);
  // The code this form is EDITING, i.e. one that came out of the post. Only such
  // a code may be re-pointed on save: re-pointing a code we merely reused for a
  // new post would silently redirect the older posts that share it.
  const [editingCode, setEditingCode] = useState<string | null>(null);

  const editing = !!initial?.editing;

  const authHeader = (): Record<string, string> => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v');
      const t = raw ? JSON.parse(raw)?.api_token : null;
      return t ? {Authorization: `Bearer ${t}`} : {};
    } catch (_) {
      return {};
    }
  };

  // Reset to whatever opened us. Keyed on `open` so closing and re-opening on a
  // different link doesn't leave the previous link's values behind.
  useEffect(() => {
    if (!open) return;
    setPhone(initial?.phone ? localiseWaPhone(initial.phone) : '');
    setMessage(initial?.message || '');
    setPrefix(initial?.prefix ?? DEFAULT_PREFIX);
    setActivePresetId(null);
    setShowSave(false);
    setSaveLabel('');
    const code = initial?.code || null;
    setEditingCode(code);
    setShorten(!!code);
    setShort(null);
    setTimeout(() => phoneRef.current?.focus(), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Opened on a short link that's already in the post: the URL carries only the
  // code, so fill the form from the server.
  useEffect(() => {
    if (!open || !initial?.code) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/wa-short-link/${initial.code}`, {headers: authHeader()})
      .then(({data}) => {
        if (cancelled || !data?.ok) return;
        setPhone(localiseWaPhone(data.phone || ''));
        setMessage(data.message || '');
        setShort({code: data.code, url: data.url});
      })
      .catch(() => {
        // Someone else's code, or one that's been deleted. Leave the form empty
        // and drop back to building a fresh link rather than editing a stranger's.
        if (!cancelled) { setEditingCode(null); setShorten(false); }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.code]);

  useEffect(() => {
    if (!open) return;
    setLoadingPresets(true);
    axios
      .get(`${API_URL}/api/wa-link-presets`, {headers: authHeader()})
      .then(({data}) => setPresets(data?.items || []))
      .catch(() => setPresets([]))
      .finally(() => setLoadingPresets(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The values moved after minting, so the code we're holding no longer describes
  // what would be inserted — a different number/message gets its own code. Drop it
  // and mint again on insert. Not while editing a code that's already in the post:
  // that one is re-pointed rather than replaced, so it survives any edit.
  useEffect(() => {
    if (editingCode) return;
    setShort(null);
  }, [phone, message, editingCode]);

  // Esc closes — the builder sits on top of the post modal, and the post modal's
  // own Esc handler would otherwise close the whole editor underneath it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const link = useMemo(() => buildWaLink(phone, message), [phone, message]);
  // What the preview shows: the short link once it exists, otherwise the raw one.
  // With shortening on but nothing minted yet the preview says so rather than
  // showing a link that isn't what will be inserted.
  const previewLink = shorten ? (short?.url || '') : link;
  const digits = normaliseWaPhone(phone);
  // 972 + 9 digits for a mobile/landline; anything shorter is still being typed.
  const phoneValid = digits.length >= 11 && digits.length <= 15;

  // Mint, reuse or re-point the short link for what's currently in the form.
  // Returns null on failure so callers can keep the modal open instead of
  // inserting a link that doesn't exist.
  const mintShort = async (): Promise<WaShortLink | null> => {
    if (!phoneValid) return null;
    setShortening(true);
    try {
      const body: Record<string, string> = {phone: digits, message};
      if (editingCode) body.code = editingCode;
      const {data} = await axios.post(`${API_URL}/api/wa-short-link`, body, {headers: authHeader()});
      if (!data?.ok || !data?.url) throw new Error(data?.error || 'failed');
      const s = {code: data.code, url: data.url};
      setShort(s);
      return s;
    } catch (e: any) {
      alert('קיצור הקישור נכשל: ' + (e?.response?.data?.error || e?.message));
      return null;
    } finally {
      setShortening(false);
    }
  };

  // Mint on switch-on so the preview shows the real link and "בדקו את הקישור"
  // works. Insert mints again with the final values — same code, so editing the
  // number after flipping the switch still ends up pointing where it should.
  const toggleShorten = async (next: boolean) => {
    setShorten(next);
    if (next && !short && phoneValid) await mintShort();
  };

  // Mint last, with the final values, so a number edited after the switch was
  // flipped is what the code actually points at. A failed mint keeps the modal
  // open — inserting the raw link "as a fallback" would quietly give the user
  // the 272-character URL they just asked not to have.
  const handleInsert = async () => {
    if (!phoneValid) return;
    const values = {phone: digits, message, prefix};
    if (!shorten) {
      onInsert(buildWaBlock({phone, message, prefix}), values);
      return;
    }
    const s = await mintShort();
    if (!s) return;
    onInsert(buildLinkBlock(s.url, prefix), values);
  };

  const applyPreset = (p: WaLinkPreset) => {
    setPhone(localiseWaPhone(p.phone));
    setMessage(p.message || '');
    setPrefix(p.prefix || '');
    setActivePresetId(p._id);
    setShowSave(false);
    // A preset remembers whether it was shortened, so someone who always shortens
    // doesn't re-tick the box on every post. Applying one never re-points the
    // code being edited, so drop out of edit mode.
    setEditingCode(null);
    setShorten(!!p.shorten);
  };

  const savePreset = async () => {
    if (!phoneValid) return;
    setSaving(true);
    try {
      const body = {label: saveLabel.trim(), phone: digits, message, prefix, shorten};
      if (activePresetId) {
        const {data} = await axios.put(`${API_URL}/api/wa-link-presets/${activePresetId}`, body, {
          headers: authHeader(),
        });
        if (data?.item) setPresets((list) => list.map((p) => (p._id === data.item._id ? data.item : p)));
      } else {
        const {data} = await axios.post(`${API_URL}/api/wa-link-presets`, body, {headers: authHeader()});
        if (data?.item) {
          setPresets((list) => [data.item, ...list]);
          setActivePresetId(data.item._id);
        }
      }
      setShowSave(false);
      setSaveLabel('');
    } catch (e: any) {
      alert('שמירת הקישור נכשלה: ' + (e?.response?.data?.error || e?.message));
    }
    setSaving(false);
  };

  const deletePreset = async (p: WaLinkPreset) => {
    if (!window.confirm(`למחוק את "${p.label || localiseWaPhone(p.phone)}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/wa-link-presets/${p._id}`, {headers: authHeader()});
      setPresets((list) => list.filter((x) => x._id !== p._id));
      if (activePresetId === p._id) setActivePresetId(null);
    } catch (e: any) {
      alert('המחיקה נכשלה: ' + (e?.response?.data?.error || e?.message));
    }
  };

  if (!open) return null;

  const label: React.CSSProperties = {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#65676b', marginBottom: 6,
  };
  const input: React.CSSProperties = {
    width: '100%', padding: '9px 11px', border: '1px solid #dfe1e6', borderRadius: 8,
    fontSize: 14, outline: 'none', background: '#fff',
  };

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100010, background: 'rgba(0,0,0,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        dir="rtl"
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,.28)', overflow: 'hidden',
        }}
      >
        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid #eef0f2',
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700}}>
            <span>💬</span>
            <span>{editing ? 'עריכת קישור הוואטסאפ' : 'יצירת קישור וואטסאפ'}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#8a8d91'}}
          >
            ×
          </button>
        </div>

        <div style={{display: 'flex', gap: 0, flex: 1, minHeight: 0}}>
          {/* saved presets */}
          <div style={{
            width: 220, flexShrink: 0, borderInlineEnd: '1px solid #eef0f2',
            background: '#fafbfc', padding: '14px 12px', overflowY: 'auto',
          }}>
            <div style={{fontSize: 12, fontWeight: 700, color: '#8a8d91', marginBottom: 10}}>
              קישורים שמורים
            </div>
            {loadingPresets && <div style={{fontSize: 12, color: '#8a8d91'}}>טוען…</div>}
            {!loadingPresets && !presets.length && (
              <div style={{fontSize: 12, color: '#8a8d91', lineHeight: 1.6}}>
                עדיין אין קישורים שמורים. מלאו את הפרטים ולחצו "שמור קישור" כדי להשתמש בו שוב בפוסטים הבאים.
              </div>
            )}
            {presets.map((p) => (
              <div
                key={p._id}
                style={{
                  border: activePresetId === p._id ? '1px solid #25d366' : '1px solid #e4e6ea',
                  background: activePresetId === p._id ? '#f0fdf4' : '#fff',
                  borderRadius: 8, padding: '8px 10px', marginBottom: 8,
                }}
              >
                <div
                  onClick={() => applyPreset(p)}
                  style={{cursor: 'pointer'}}
                  title="השתמש בקישור הזה"
                >
                  <div style={{fontSize: 13, fontWeight: 600, color: '#1c1e21', marginBottom: 2}}>
                    {p.label || 'ללא שם'}
                  </div>
                  <div style={{fontSize: 12, color: '#8a8d91', direction: 'ltr', textAlign: 'right', fontFamily: 'monospace'}}>
                    {localiseWaPhone(p.phone)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deletePreset(p)}
                  style={{
                    marginTop: 6, border: 'none', background: 'transparent', color: '#d32f2f',
                    fontSize: 11, cursor: 'pointer', padding: 0,
                  }}
                >
                  מחק
                </button>
              </div>
            ))}
          </div>

          {/* form */}
          <div style={{flex: 1, minWidth: 0, padding: '16px 18px', overflowY: 'auto'}}>
            <div style={{marginBottom: 14}}>
              <label style={label}>מספר הוואטסאפ</label>
              <input
                ref={phoneRef}
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="052-455-1715"
                style={{...input, textAlign: 'left', fontFamily: 'monospace'}}
              />
              {!!phone && !phoneValid && (
                <div style={{marginTop: 5, fontSize: 12, color: '#d32f2f'}}>
                  המספר לא נראה תקין — הזינו מספר ישראלי מלא (למשל 052-455-1715).
                </div>
              )}
            </div>

            <div style={{marginBottom: 14}}>
              <label style={label}>הודעת הפתיחה של הלקוח</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="היי, ראיתי את המודעה ואשמח לקבל פרטים נוספים"
                style={{...input, resize: 'vertical', lineHeight: 1.5}}
              />
              <div style={{marginTop: 5, fontSize: 12, color: '#8a8d91'}}>
                זו ההודעה שתופיע ללקוח מוכנה לשליחה ברגע שילחץ על הקישור.
              </div>
            </div>

            <div style={{marginBottom: 14}}>
              <label style={label}>הטקסט שמופיע מעל הקישור (לא חובה)</label>
              <input
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder={DEFAULT_PREFIX}
                style={input}
              />
            </div>

            {/* shorten the link */}
            <div style={{
              border: '1px solid #e4e6ea', borderRadius: 10, background: '#fff',
              padding: '10px 12px', marginBottom: 14,
            }}>
              <label style={{display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer'}}>
                <input
                  type="checkbox"
                  checked={shorten}
                  onChange={(e) => toggleShorten(e.target.checked)}
                  style={{marginTop: 3, width: 16, height: 16, cursor: 'pointer', flexShrink: 0}}
                />
                <span>
                  <span style={{fontSize: 13, fontWeight: 600, color: '#1c1e21'}}>
                    קצרו את הקישור
                    {link && !shorten && (
                      <span style={{fontWeight: 400, color: '#8a8d91'}}> ({link.length} תווים ← {SHORT_LINK_SAMPLE_LEN})</span>
                    )}
                  </span>
                  <span style={{display: 'block', marginTop: 3, fontSize: 12, color: '#8a8d91', lineHeight: 1.55}}>
                    הודעת הפתיחה נשמרת אצלנו במקום בתוך הקישור, כך שהפוסט נשאר נקי.
                    בנוסף — תוכלו להחליף את המספר או את ההודעה גם אחרי הפרסום, וכל הפוסטים שכבר יצאו יתעדכנו.
                  </span>
                </span>
              </label>
              {shortening && (
                <div style={{marginTop: 6, fontSize: 12, color: '#8a8d91'}}>מקצר…</div>
              )}
              {shorten && short && !!short.code && (
                <div style={{marginTop: 6, fontSize: 12, color: '#8a8d91'}}>
                  הקוד <b style={{fontFamily: 'monospace'}}>{short.code}</b>
                  {editingCode ? ' — עדכון של הקישור שכבר בפוסט.' : ' — קישור חדש.'}
                </div>
              )}
            </div>

            {/* preview */}
            <div style={{
              border: '1px solid #e4e6ea', borderRadius: 10, background: '#fafbfc',
              padding: '10px 12px', marginBottom: 14,
            }}>
              <div style={{fontSize: 12, fontWeight: 700, color: '#8a8d91', marginBottom: 6}}>
                כך זה ייראה בפוסט
              </div>
              {previewLink ? (
                <>
                  {!!prefix.trim() && (
                    <div style={{fontSize: 14, color: '#1c1e21', marginBottom: 2}}>{prefix.trim()}</div>
                  )}
                  <div style={{
                    fontSize: 12, color: '#1877f2', direction: 'ltr', textAlign: 'left',
                    wordBreak: 'break-all', fontFamily: 'monospace',
                  }}>
                    {previewLink}
                  </div>
                  <a
                    href={previewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{display: 'inline-block', marginTop: 8, fontSize: 12, color: '#25d366', fontWeight: 600}}
                  >
                    🔗 בדקו את הקישור
                  </a>
                </>
              ) : (
                <div style={{fontSize: 13, color: '#8a8d91'}}>
                  {shorten && phoneValid
                    ? 'הקישור הקצר ייווצר בעת ההוספה לפוסט.'
                    : 'הזינו מספר טלפון כדי לראות את הקישור.'}
                </div>
              )}
            </div>

            {/* save as preset */}
            {!showSave ? (
              <button
                type="button"
                onClick={() => { setShowSave(true); setSaveLabel(presets.find((p) => p._id === activePresetId)?.label || ''); }}
                disabled={!phoneValid}
                style={{
                  border: '1px solid #dfe1e6', background: '#fff', borderRadius: 8,
                  padding: '7px 12px', fontSize: 13, fontWeight: 600,
                  cursor: phoneValid ? 'pointer' : 'not-allowed', color: '#65676b',
                }}
              >
                ⭐ {activePresetId ? 'עדכן את הקישור השמור' : 'שמור קישור לשימוש חוזר'}
              </button>
            ) : (
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                <input
                  value={saveLabel}
                  onChange={(e) => setSaveLabel(e.target.value)}
                  placeholder="שם לקישור (למשל: דירות חולון)"
                  style={{...input, flex: 1}}
                />
                <button
                  type="button"
                  onClick={savePreset}
                  disabled={saving}
                  style={{
                    border: 'none', background: '#25d366', color: '#fff', borderRadius: 8,
                    padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {saving ? 'שומר…' : 'שמור'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSave(false)}
                  style={{border: 'none', background: 'transparent', color: '#8a8d91', fontSize: 13, cursor: 'pointer'}}
                >
                  ביטול
                </button>
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-start', gap: 10,
          padding: '12px 18px', borderTop: '1px solid #eef0f2', background: '#fafbfc',
        }}>
          <button
            type="button"
            disabled={!phoneValid || shortening}
            onClick={handleInsert}
            style={{
              border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 700,
              background: phoneValid && !shortening ? '#25d366' : '#c7ccd1', color: '#fff',
              cursor: phoneValid && !shortening ? 'pointer' : 'not-allowed',
            }}
          >
            {shortening ? 'מקצר…' : editing ? 'עדכן את הקישור' : 'הוסף לפוסט'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid #dfe1e6', borderRadius: 8, padding: '10px 18px',
              fontSize: 14, fontWeight: 600, background: '#fff', color: '#65676b', cursor: 'pointer',
            }}
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsappLinkModal;
